import {
  Box,
  Heading,
  HStack,
  Stack,
  Text,
  Image,
  Divider,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Collapse,
  Switch,
  Button,
  Link,
  Wrap,
  WrapItem,
  Badge,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useLocation } from "react-router-dom";
import axios from "axios";
import PaymentCheckoutTest from "../../CheckoutPage/PaymentCheckoutTest";
import { Car, HireDraft } from "./car.types";
import { normalizeCarImages } from "./dummyCarImages";
import AutocompleteInput from "../../../LocationsModule/PlaceAutocompleteInput";
import { useJsApiLoader } from "@react-google-maps/api";
import { ChangeEvent, useRef, useState, useEffect, useMemo } from "react";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import { uploadListingImagesToFirebase } from "../Accomodations/uploadListingImagesToFirebase";
import { useAuth } from "../../../auth/AuthContext";
import OrderPricingBreakdownCard from "../../CheckoutPage/OrderPricingBreakdownCard";
const removeTrailingSlash = (value: string) => value.replace(/\/+$/, "");
const cardsBorderRadii = "8px";
const getBackendBaseUrl = () => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();
    const isLocal =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "0.0.0.0";
    if (isLocal) {
      return removeTrailingSlash(
        String(
          process.env.REACT_APP_LOCAL_BACKEND_URL || "http://localhost:5000",
        ).trim(),
      );
    }
  }
  return removeTrailingSlash(
    String(
      process.env.REACT_APP_BACKEND_URL ||
        process.env.REACT_APP_PUBLIC_API_URL ||
        "https://api.pickdengu.mw",
    ).trim(),
  );
};
const BACKEND_BASE_URL = getBackendBaseUrl();
const ORDERS_API = `${BACKEND_BASE_URL}/api/v1/orders`;
const CARS_API = `${BACKEND_BASE_URL}/api/v1/travel/cars`;
const MAX_LICENSE_FILES = 2;
const MAX_IDENTITY_FILES = 2;
const MAX_DOCUMENT_SIZE_BYTES = 10 * 1024 * 1024;
const SERVICE_FEE_RATE = 0.05;
const GATEWAY_FEE_RATE = 0.025;
const SELLER_COMMISSION_RATE = 0.1;
const DRIVER_EARNING_RATE = 0.7;
const roundMoney = (value: number) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
type CreatedOrderPaymentDetails = {
  orderId: string;
  amount: number;
  currency: string;
};
type PreviewType = "image" | "pdf" | "other";
type LicensePreviewItem = {
  file: File;
  previewUrl: string;
  type: PreviewType;
  name: string;
};
const isAllowedComplianceFile = (file: File) => {
  const fileName = file.name.toLowerCase();
  const isImage = file.type.startsWith("image/");
  const isPdf = file.type === "application/pdf" || fileName.endsWith(".pdf");
  return isImage || isPdf;
};
const getComplianceFileError = (file: File, documentName: string) => {
  if (!isAllowedComplianceFile(file)) {
    return `${documentName} must be an image or PDF file.`;
  }
  if (!file.size) {
    return `${documentName} cannot be empty.`;
  }
  if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
    return `${documentName} must be 10 MB or smaller.`;
  }
  return "";
};
const getDrivingLicenceNumberError = (value: string) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "Driving licence number is required.";
  }
  if (normalized.length < 4) {
    return "Driving licence number is too short.";
  }
  if (normalized.length > 40) {
    return "Driving licence number must be 40 characters or fewer.";
  }
  if (!/^[A-Za-z0-9][A-Za-z0-9 /.-]*[A-Za-z0-9]$/.test(normalized)) {
    return "Use only letters, numbers, spaces, /, . or - in the licence number.";
  }
  return "";
};
const cleanUserId = (value: any) => {
  const id = String(value || "").trim();
  if (!id) return "";
  if (id.toLowerCase() === "guest") return "";
  return id;
};
const getSafeBuyerUserId = (value: any) => {
  const id = String(value || "").trim();
  return id || "guest";
};
const getCarOwnerUserId = (car: any) => {
  return cleanUserId(
    car?.ownerId ||
      car?.ownerUserId ||
      car?.sellerUserId ||
      car?.createdBy ||
      car?.userId ||
      "",
  );
};
const getCarOwnerName = (car: any) => {
  return String(
    car?.ownerName ||
      car?.sellerName ||
      car?.sellerBusinessName ||
      car?.businessName ||
      [car?.brand || car?.make, car?.model].filter(Boolean).join(" ") ||
      "Car Hire Owner",
  ).trim();
};
const getCarOwnerPhoneNumber = (car: any) => {
  return String(
    car?.ownerPhoneNumber ||
      car?.sellerPhoneNumber ||
      car?.whatsappPhone ||
      car?.whatsappNumber ||
      car?.phoneNumber ||
      car?.phone ||
      "",
  ).trim();
};
const getCarHireNotificationPolicy = () => ({
  sendCustomerCreated: true,
  sendSellerCreatedStatus: true,
  suppressSellerConfirmReject: true,
  suppressSellerAvailabilityConfirmation: true,
  sellerNotificationMode: "status_view_only",
  sellerCreatedTemplateKey: "seller_order_status",
  sendSellerPaidStatus: true,
  sendCustomerInvoiceOnCreated: true,
  sendCustomerInvoiceOnPaid: true,
  sendSellerInvoiceOnPaid: true,
  sellerPaidTemplateKey: "order_payment_invoice",
  customerPaidTemplateKey: "order_payment_confirmed",
});
export default function CarHireCheckoutPage() {
  const toast = useToast();
  const location = useLocation();
  const { theme } = useCustomTheme();
  const { user } = useAuth();
  const state = (location.state || {}) as {
    car?: Car;
    hire?: HireDraft;
  };
  const car = state.car;
  const hire = state.hire;
  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });
  const authUser = user as any;
  const carData = car as any;
  const loggedInUserId = authUser?.uid || "";
  // Customer/buyer can be a guest.
  const safeBuyerUserId = getSafeBuyerUserId(loggedInUserId);
  // Owner/seller must come from the car listing.
  const ownerUserId = getCarOwnerUserId(carData);
  const ownerName = getCarOwnerName(carData);
  const ownerPhoneNumber = getCarOwnerPhoneNumber(carData);
  const loggedInEmail = authUser?.email || "support@pickdengu.mw";
  const [licenseImageUrl, setLicenseImageUrl] = useState(
    hire?.licenseImageUrl || "",
  );
  const [licenseDocumentUrls, setLicenseDocumentUrls] = useState<string[]>(
    hire?.licenseImageUrl ? [hire.licenseImageUrl] : [],
  );
  const [licenseFiles, setLicenseFiles] = useState<LicensePreviewItem[]>([]);
  const [identityDocumentUrls, setIdentityDocumentUrls] = useState<string[]>(
    Array.isArray((hire as any)?.identityDocumentUrls)
      ? (hire as any).identityDocumentUrls
      : [],
  );
  const [identityFiles, setIdentityFiles] = useState<LicensePreviewItem[]>([]);
  const [damageAck, setDamageAck] = useState(!!hire?.damageAcknowledged);
  const [deliveryRequested, setDeliveryRequested] = useState(
    !!hire?.deliveryRequested,
  );
  const [deliveryAddress, setDeliveryAddress] = useState(
    hire?.deliveryAddress?.text || "",
  );
  const [deliveryCoords, setDeliveryCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(
    hire?.deliveryAddress?.lat && hire?.deliveryAddress?.lng
      ? {
          lat: hire.deliveryAddress.lat,
          lng: hire.deliveryAddress.lng,
        }
      : null,
  );
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const [showBookingSummary, setShowBookingSummary] = useState(false);
  const licenseInputRef = useRef<HTMLInputElement | null>(null);
  const identityInputRef = useRef<HTMLInputElement | null>(null);
  const createdBookingIdRef = useRef<string | null>(null);
  const createdOrderIdRef = useRef<string | null>(null);
  const createdOrderPaymentRef = useRef<CreatedOrderPaymentDetails | null>(
    null,
  );
  const createOrderPromiseRef =
    useRef<Promise<CreatedOrderPaymentDetails | null> | null>(null);
  const inputStyles = {
    borderColor: theme.inputBorder,
    bg: theme.inputBg,
    color: theme.text,
    _placeholder: { color: theme.inputPlaceholder },
    _hover: { borderColor: theme.inputBorderFocus },
    _focus: {
      borderColor: theme.inputBorderFocus,
      boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
    },
  };
  useEffect(() => {
    return () => {
      licenseFiles.forEach((item) => {
        if (item.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
      identityFiles.forEach((item) => {
        if (item.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, [licenseFiles, identityFiles]);
  const images = useMemo(
    () => normalizeCarImages(car?.images, car?.imageUrl),
    [car?.images, car?.imageUrl],
  );
  const breakdown = hire?.quote?.breakdown;
  /*
   * Keep the page, saved order and PayChangu amount on the same calculation.
   *
   * The car quote already contains weekend pricing, long-stay/promo discounts,
   * insurance and an optional vehicle-delivery fee. The order item therefore
   * uses the quote amount excluding delivery as its service subtotal. The
   * shared order-pricing code then adds the same 5% service fee shown here.
   */
  const pricingBreakdown = useMemo(() => {
    const hireDays = Math.max(1, Number(breakdown?.days || 1));
    const fallbackProductSubtotal = roundMoney(
      Number(car?.pricePerDay || 0) * hireDays,
    );
    const deliveryFee = deliveryRequested
      ? roundMoney(Number(breakdown?.deliveryFee || 0))
      : 0;
    const quotedTotalDue = Number(breakdown?.totalDue);
    const hasValidQuoteTotal =
      Boolean(breakdown) &&
      Number.isFinite(quotedTotalDue) &&
      quotedTotalDue >= 0;
    const productSubtotal = hasValidQuoteTotal
      ? roundMoney(Math.max(0, quotedTotalDue - deliveryFee))
      : fallbackProductSubtotal;
    const serviceFee = roundMoney(productSubtotal * SERVICE_FEE_RATE);
    const discountAmount = 0;
    const checkoutTotal = roundMoney(
      Math.max(0, productSubtotal + serviceFee + deliveryFee - discountAmount),
    );
    const gatewayFee = roundMoney(checkoutTotal * GATEWAY_FEE_RATE);
    const sellerCommissionTotal = roundMoney(
      productSubtotal * SELLER_COMMISSION_RATE,
    );
    const sellerNetPayableTotal = roundMoney(
      Math.max(0, productSubtotal - sellerCommissionTotal),
    );
    const driverEarningTotal = deliveryRequested
      ? roundMoney(deliveryFee * DRIVER_EARNING_RATE)
      : 0;
    const deliveryMargin = roundMoney(deliveryFee - driverEarningTotal);
    const platformNetRevenue = roundMoney(
      serviceFee + sellerCommissionTotal + deliveryMargin - gatewayFee,
    );
    return {
      currency: "MWK",
      productSubtotal,
      serviceFee,
      deliveryFee,
      discountAmount,
      gatewayFee,
      total: checkoutTotal,
      sellerCommissionTotal,
      sellerNetPayableTotal,
      driverEarningTotal,
      deliveryMargin,
      platformNetRevenue,
      deliveryDistanceKm: 0,
      totalActualWeightKg: 0,
      totalVolumetricWeightKg: 0,
      billableWeightKg: 0,
      bulkySurcharge: 0,
    };
  }, [
    breakdown?.days,
    breakdown?.deliveryFee,
    breakdown?.totalDue,
    car?.pricePerDay,
    deliveryRequested,
  ]);
  const checkoutAmount = Number(pricingBreakdown.total || 0);
  if (!car || !hire) {
    return (
      <Box p={6} color={theme.text}>
        <Heading size="md" color={theme.text}>
          Missing checkout data
        </Heading>
        <Text mt={2} color={theme.textMuted}>
          Please go back and select a car again.
        </Text>
      </Box>
    );
  }
  const checkoutPickupDate = new Date(hire.pickupAt);
  const checkoutReturnDate = new Date(hire.returnAt);
  const checkoutDateError = !hire.pickupAt
    ? "Pickup date and time are required."
    : !hire.returnAt
      ? "Return date and time are required."
      : Number.isNaN(checkoutPickupDate.getTime())
        ? "Pickup date and time are invalid."
        : Number.isNaN(checkoutReturnDate.getTime())
          ? "Return date and time are invalid."
          : checkoutReturnDate <= checkoutPickupDate
            ? "Return date and time must be after pickup."
            : "";
  if (checkoutDateError) {
    return (
      <Box p={6} color={theme.text}>
        <Heading size="md" color={theme.text}>
          Check your hire dates
        </Heading>
        <Text mt={2} color={theme.danger} fontWeight="600">
          {checkoutDateError}
        </Text>
        <Text mt={1} color={theme.textMuted}>
          Please go back to the Hire window and choose valid pickup and return
          dates before continuing to payment.
        </Text>
      </Box>
    );
  }
  const getPreviewType = (file: File): PreviewType => {
    if (file.type.startsWith("image/")) return "image";
    if (
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf")
    ) {
      return "pdf";
    }
    return "other";
  };
  const handlePickLicenseFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const validFiles = picked.filter((file) => {
      const validationError = getComplianceFileError(file, "Driving licence");
      if (validationError) {
        toast({
          title: "Invalid driving licence document",
          description: `${file.name}: ${validationError}`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return false;
      }
      return true;
    });
    const remainingSlots = MAX_LICENSE_FILES - licenseFiles.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);
    if (!filesToAdd.length) {
      if (validFiles.length > 0) {
        toast({
          title: `You can only upload ${MAX_LICENSE_FILES} driving licence document(s)`,
          status: "warning",
        });
      }
      e.target.value = "";
      return;
    }
    const newItems: LicensePreviewItem[] = filesToAdd.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      type: getPreviewType(file),
      name: file.name,
    }));
    setLicenseFiles((prev) => [...prev, ...newItems]);
    if (validFiles.length > remainingSlots) {
      toast({
        title: `Only ${MAX_LICENSE_FILES} driving licence document(s) allowed`,
        description: "Extra files were ignored.",
        status: "warning",
      });
    }
    e.target.value = "";
  };
  const removeLicenseAt = (index: number) => {
    setLicenseFiles((prev) => {
      const item = prev[index];
      if (item?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(item.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  };
  const clearUploadedLicenseUrls = () => {
    setLicenseImageUrl("");
    setLicenseDocumentUrls([]);
  };
  const handlePickIdentityFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const validFiles = picked.filter((file) => {
      const validationError = getComplianceFileError(file, "ID or passport");
      if (validationError) {
        toast({
          title: "Invalid ID or passport document",
          description: `${file.name}: ${validationError}`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return false;
      }
      return true;
    });
    const remainingSlots = MAX_IDENTITY_FILES - identityFiles.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);
    if (!filesToAdd.length) {
      if (validFiles.length > 0) {
        toast({
          title: `You can only upload ${MAX_IDENTITY_FILES} ID/passport document(s)`,
          status: "warning",
        });
      }
      e.target.value = "";
      return;
    }
    const newItems: LicensePreviewItem[] = filesToAdd.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      type: getPreviewType(file),
      name: file.name,
    }));
    setIdentityFiles((prev) => [...prev, ...newItems]);
    if (validFiles.length > remainingSlots) {
      toast({
        title: `Only ${MAX_IDENTITY_FILES} ID/passport document(s) allowed`,
        description: "Extra files were ignored.",
        status: "warning",
      });
    }
    e.target.value = "";
  };
  const removeIdentityAt = (index: number) => {
    setIdentityFiles((prev) => {
      const item = prev[index];
      if (item?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(item.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  };
  const clearUploadedIdentityUrls = () => {
    setIdentityDocumentUrls([]);
  };
  const uploadLicenseDocsIfNeeded = async (): Promise<string[]> => {
    if (!licenseFiles.length) {
      return licenseDocumentUrls;
    }
    const key = `${safeBuyerUserId || "guest"}-${
      car._id || hire.carId || Date.now()
    }`;
    const files = licenseFiles.map((item) => item.file);
    const uploaded = await uploadListingImagesToFirebase(files, key, "cars");
    return uploaded || [];
  };
  const uploadIdentityDocsIfNeeded = async (): Promise<string[]> => {
    if (!identityFiles.length) {
      return identityDocumentUrls;
    }
    const key = `${safeBuyerUserId || "guest"}-${
      car._id || hire.carId || Date.now()
    }-identity`;
    const files = identityFiles.map((item) => item.file);
    const uploaded = await uploadListingImagesToFirebase(files, key, "cars");
    return uploaded || [];
  };
  const drivingLicenceNumber = String(hire?.licenseNumber || "").trim();
  const drivingLicenceNumberError =
    getDrivingLicenceNumberError(drivingLicenceNumber);
  const hasDrivingLicenceDocument =
    licenseFiles.length > 0 || licenseDocumentUrls.length > 0;
  const hasIdentityDocument =
    identityFiles.length > 0 || identityDocumentUrls.length > 0;
  const isComplianceReady =
    !drivingLicenceNumberError &&
    hasDrivingLicenceDocument &&
    hasIdentityDocument &&
    damageAck;

  const isPaymentTransitioning =
    isSubmittingBooking || Boolean(createdOrderPaymentRef.current);
  const createCarHireOrder =
    async (): Promise<CreatedOrderPaymentDetails | null> => {
      if (createdOrderPaymentRef.current && createdBookingIdRef.current) {
        return createdOrderPaymentRef.current;
      }
      try {
        if (drivingLicenceNumberError) {
          throw new Error(drivingLicenceNumberError);
        }
        if (!hasDrivingLicenceDocument) {
          throw new Error(
            "A driving licence document is required before payment.",
          );
        }
        if (!hasIdentityDocument) {
          throw new Error(
            "A valid ID or passport document is required before payment.",
          );
        }
        if (!damageAck) {
          throw new Error("Please acknowledge the damage policy.");
        }
        if (!ownerUserId) {
          throw new Error(
            "This car listing is missing a valid owner ID. Please edit and save the car again from the owner account before accepting bookings.",
          );
        }
        const pickupDateTime = new Date(hire.pickupAt);
        const returnDateTime = new Date(hire.returnAt);
        if (!hire.pickupAt || Number.isNaN(pickupDateTime.getTime())) {
          throw new Error("A valid pickup date and time is required.");
        }
        if (!hire.returnAt || Number.isNaN(returnDateTime.getTime())) {
          throw new Error("A valid return date and time is required.");
        }
        if (returnDateTime <= pickupDateTime) {
          throw new Error("Return date and time must be after pickup.");
        }
        if (pickupDateTime.getTime() < Date.now() - 5 * 60 * 1000) {
          throw new Error("Pickup date and time cannot be in the past.");
        }
        setIsSubmittingBooking(true);
        const uploadedLicenseUrls = await uploadLicenseDocsIfNeeded();
        const primaryLicenseUrl =
          uploadedLicenseUrls[0] || licenseImageUrl || "";
        if (!primaryLicenseUrl || uploadedLicenseUrls.length === 0) {
          throw new Error(
            "Driving licence upload failed. Please select the document again.",
          );
        }
        const uploadedIdentityUrls = await uploadIdentityDocsIfNeeded();
        if (uploadedIdentityUrls.length === 0) {
          throw new Error(
            "ID or passport upload failed. Please select the document again.",
          );
        }
        const notificationPolicy = getCarHireNotificationPolicy();
        let orderId = createdOrderIdRef.current;
        let paymentDetails = createdOrderPaymentRef.current;
        /*
         * Match product and delivery checkout:
         * create and persist the pending order first, then use its order ID
         * when creating the car-hire booking. This prevents orphan bookings
         * when order creation or payment initialization fails.
         */
        if (!orderId || !paymentDetails) {
          const pickupAddressData = hire.pickupAddress as any;
          const pickupLocation =
            pickupAddressData &&
            typeof pickupAddressData.lat === "number" &&
            typeof pickupAddressData.lng === "number"
              ? {
                  lat: pickupAddressData.lat,
                  lng: pickupAddressData.lng,
                }
              : undefined;
          const order: any = {
            currency: "MWK",
            category: "car_hire",
            serviceCategory: "car_hire",
            orderType: "car_hire",
            source: "web",
            channel: "web",
            // Customer/buyer. No login required.
            userId: safeBuyerUserId,
            // Car owner/seller. This must come from the listing creator/owner.
            ownerUserId,
            sellerUserId: ownerUserId,
            ownerName,
            sellerName: ownerName,
            ownerPhoneNumber,
            sellerPhoneNumber: ownerPhoneNumber,
            notificationPolicy,
            items: [
              {
                _id: car._id,
                productId: car._id,
                sellerUserId: ownerUserId,
                ownerId: ownerUserId,
                userId: ownerUserId,
                sellerBusinessName: ownerName,
                sellerName: ownerName,
                ownerName,
                sellerPhoneNumber: ownerPhoneNumber,
                ownerPhoneNumber,
                title: `Car Hire: ${car.brand || car.make} ${car.model}`,
                description: [
                  `Pickup: ${new Date(hire.pickupAt).toLocaleString()}`,
                  `Return: ${new Date(hire.returnAt).toLocaleString()}`,
                  `Pickup Address: ${hire.pickupAddress?.text || ""}`,
                  deliveryRequested
                    ? `Delivery Address: ${deliveryAddress}`
                    : "",
                  `Insurance: ${hire.insurancePlan}`,
                  hire.promoCode ? `Promo: ${hire.promoCode}` : "",
                  `Notes: ${hire.notes || ""}`,
                ]
                  .filter(Boolean)
                  .join(" | "),
                category: "car_hire",
                serviceCategory: "car_hire",
                orderType: "car_hire",
                imageUrl: images[0] || "",
                // One car-hire service item. Its price is the complete quoted
                // hire subtotal excluding the separately supplied delivery fee.
                // This makes the backend reproduce the exact checkout amount.
                price: Number(pricingBreakdown.productSubtotal || 0),
                quantity: 1,
                selectedVariants: [],
                variantSummary: "",
                isPhysical: false,
                weightKg: 0,
                lengthCm: 0,
                widthCm: 0,
                heightCm: 0,
                bulky: false,
                meta: {
                  carId: car._id,
                  bookingId: "",
                  ownerUserId,
                  sellerUserId: ownerUserId,
                  ownerName,
                  sellerName: ownerName,
                  ownerPhoneNumber,
                  sellerPhoneNumber: ownerPhoneNumber,
                  category: "car_hire",
                  serviceCategory: "car_hire",
                  orderType: "car_hire",
                  hireDays: Number(breakdown?.days || 1),
                  basePerDay: Number(
                    breakdown?.basePerDay || car.pricePerDay || 0,
                  ),
                  quoteBreakdown: breakdown || {},
                },
              },
            ],
            total: checkoutAmount,
            pricingBreakdown,
            deliveryFee: Number(pricingBreakdown.deliveryFee || 0),
            discountAmount: Number(pricingBreakdown.discountAmount || 0),
            checkoutMode: deliveryRequested ? "delivery" : "pickup",
            fromAddress: hire.pickupAddress?.text || "",
            toAddress: deliveryRequested ? deliveryAddress : "",
            fromLocation: pickupLocation,
            toLocation:
              deliveryRequested && deliveryCoords
                ? {
                    lat: deliveryCoords.lat,
                    lng: deliveryCoords.lng,
                  }
                : undefined,
            pickupAddress: hire.pickupAddress?.text || "",
            deliveryInstructions: hire.notes || "",
            email: loggedInEmail,
            phoneNumber: hire.phoneNumber,
            name: authUser?.displayName || "Customer",
            payment: {
              method: "",
              provider: "",
              status: "pending",
              providerPaymentId: "",
              providerCheckoutId: "",
              reference: "",
            },
            status: "pending",
            // Car hire is fulfilled by the vehicle owner. It must never create
            // a marketplace warehouse/driver dispatch job or resolve warehouses.
            skipDispatchCreation: true,
            skipWarehouseResolution: true,
            requiresWarehouse: false,
            requiresDispatch: false,
            metadata: {
              notificationPolicy,
              bookingId: "",
              carId: car._id,
              ownerUserId,
              sellerUserId: ownerUserId,
              ownerName,
              sellerName: ownerName,
              ownerPhoneNumber,
              sellerPhoneNumber: ownerPhoneNumber,
              category: "car_hire",
              serviceCategory: "car_hire",
              orderType: "car_hire",
              source: "web",
              channel: "web",
              pricingBreakdown,
              checkoutAmount,
              deliveryRequested,
              deliveryAddress: deliveryRequested ? deliveryAddress : "",
              skipWarehouseResolution: true,
              requiresWarehouse: false,
              requiresDispatch: false,
              drivingLicenceNumber,
              drivingLicenceDocumentUrls: uploadedLicenseUrls,
              identityDocumentUrls: uploadedIdentityUrls,
            },
          };
          const res = await axios.post(ORDERS_API, order);
          const createdOrder =
            res.data?.data || res.data?.order || res.data || {};
          orderId =
            createdOrder?._id ||
            res.data?._id ||
            res.data?.data?._id ||
            res.data?.order?._id ||
            null;
          if (!orderId) {
            throw new Error(
              "The order was created but no order ID was returned.",
            );
          }
          const createdOrderAmount = Number(
            createdOrder?.total ||
              createdOrder?.pricingBreakdown?.total ||
              checkoutAmount,
          );
          paymentDetails = {
            orderId,
            amount:
              Number.isFinite(createdOrderAmount) && createdOrderAmount > 0
                ? createdOrderAmount
                : checkoutAmount,
            currency: String(
              createdOrder?.currency ||
                createdOrder?.pricingBreakdown?.currency ||
                "MWK",
            )
              .trim()
              .toUpperCase(),
          };
          // Persist immediately so a booking retry reuses the same order.
          createdOrderIdRef.current = orderId;
          createdOrderPaymentRef.current = paymentDetails;
        }
        if (!orderId || !paymentDetails) {
          throw new Error("Could not prepare the car-hire order for payment.");
        }
        let bookingId = createdBookingIdRef.current;
        if (!bookingId) {
          const bookingRes = await axios.post(`${CARS_API}/hire/bookings`, {
            carId: hire.carId,
            // Customer hiring the car. Can be guest.
            userId: safeBuyerUserId,
            customerName: authUser?.displayName || "Customer",
            // Link the booking to the already-created pending order.
            orderId,
            checkoutOrderId: orderId,
            pickupAt: hire.pickupAt,
            returnAt: hire.returnAt,
            pickupAddress: hire.pickupAddress,
            deliveryRequested,
            deliveryAddress: deliveryRequested
              ? {
                  text: deliveryAddress,
                  ...(deliveryCoords || {}),
                }
              : undefined,
            phoneNumber: hire.phoneNumber,
            notes: hire.notes || "",
            age: hire.age,
            acceptedTerms: hire.acceptedTerms,
            insurancePlan: hire.insurancePlan,
            promoCode: hire.promoCode || "",
            licenseNumber: drivingLicenceNumber,
            licenseImageUrl: primaryLicenseUrl,
            licenseDocumentUrls: uploadedLicenseUrls,
            identityDocumentUrls: uploadedIdentityUrls,
            damageAcknowledged: damageAck,
          });
          bookingId =
            bookingRes.data?.booking?._id ||
            bookingRes.data?.data?._id ||
            bookingRes.data?._id ||
            null;
          if (!bookingId) {
            throw new Error(
              "The car-hire booking was not created. Please try again.",
            );
          }
          createdBookingIdRef.current = bookingId;
        }
        setLicenseImageUrl(primaryLicenseUrl);
        setLicenseDocumentUrls(uploadedLicenseUrls);
        setIdentityDocumentUrls(uploadedIdentityUrls);
        licenseFiles.forEach((item) => {
          if (item.previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(item.previewUrl);
          }
        });
        identityFiles.forEach((item) => {
          if (item.previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(item.previewUrl);
          }
        });
        setLicenseFiles([]);
        setIdentityFiles([]);
        return paymentDetails;
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Could not create the car-hire order and booking.";
        console.error("Failed to create car-hire order/booking:", {
          message,
          response: err?.response?.data,
        });
        throw new Error(message);
      } finally {
        setIsSubmittingBooking(false);
      }
    };
  const onCreateOrder =
    async (): Promise<CreatedOrderPaymentDetails | null> => {
      if (createdOrderPaymentRef.current) {
        return createdOrderPaymentRef.current;
      }
      if (createOrderPromiseRef.current) {
        return createOrderPromiseRef.current;
      }
      const promise = createCarHireOrder();
      createOrderPromiseRef.current = promise;
      try {
        return await promise;
      } finally {
        createOrderPromiseRef.current = null;
      }
    };
  return (
    <Box p={{ base: 3, md: 6 }} color={theme.text}>
      <Heading size="md" mb={4} color={theme.text}>
        Car Hire Checkout
      </Heading>
      <Stack
        direction={{ base: "column", xl: "row" }}
        align="stretch"
        spacing={6}
      >
        <Box
          flex="1"
          w="100%"
          minW={0}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowSm}
          border="1px solid"
          borderColor={theme.border}
          p={{ base: 3, md: 4 }}
        >
          <Box
            border="1px solid"
            borderColor={theme.border}
            bg={theme.surfaceAlt}
            borderRadius={cardsBorderRadii}
            overflow="hidden"
          >
            <HStack
              as="button"
              type="button"
              w="100%"
              textAlign="left"
              justify="space-between"
              align="center"
              spacing={3}
              p={{ base: 3, sm: 4 }}
              cursor="pointer"
              _hover={{ bg: theme.hover }}
              onClick={() => setShowBookingSummary((prev) => !prev)}
            >
              <Badge
                bg="transparent"
                color={theme.text}
                px={0}
                py={0}
                borderRadius={0}
                textTransform="uppercase"
                fontSize="sm"
                fontWeight="700"
              >
                Booking Summary
              </Badge>
              <HStack spacing={2} flexShrink={0}>
                <Text fontSize="sm" fontWeight="700" color={theme.textMuted}>
                  {showBookingSummary ? "Hide" : "Show"}
                </Text>
                <ChevronDownIcon
                  color={theme.textMuted}
                  transition="transform 0.2s ease"
                  transform={
                    showBookingSummary ? "rotate(180deg)" : "rotate(0deg)"
                  }
                />
              </HStack>
            </HStack>
            <Collapse in={showBookingSummary} animateOpacity>
              <Stack
                spacing={4}
                px={{ base: 3, sm: 4 }}
                pb={{ base: 3, sm: 4 }}
              >
                <Stack
                  direction={{ base: "column", sm: "row" }}
                  spacing={4}
                  align="stretch"
                >
                  <Image
                    src={images[0]}
                    alt="car"
                    w={{ base: "100%", sm: "132px" }}
                    h={{ base: "190px", sm: "132px" }}
                    flexShrink={0}
                    objectFit="cover"
                    borderRadius={cardsBorderRadii}
                    border="1px solid"
                    borderColor={theme.border}
                  />
                  <Stack spacing={2} flex="1" minW={0}>
                    <Heading
                      size="sm"
                      color={theme.text}
                      noOfLines={2}
                      wordBreak="break-word"
                    >
                      {car.brand || car.make} {car.model}
                    </Heading>
                    <Text fontSize="sm" color={theme.text}>
                      <Text as="span" fontWeight="700">
                        Pickup:
                      </Text>{" "}
                      {new Date(hire.pickupAt).toLocaleString()}
                    </Text>
                    <Text fontSize="sm" color={theme.text}>
                      <Text as="span" fontWeight="700">
                        Return:
                      </Text>{" "}
                      {new Date(hire.returnAt).toLocaleString()}
                    </Text>
                    <Text fontSize="sm" color={theme.text}>
                      <Text as="span" fontWeight="700">
                        Insurance:
                      </Text>{" "}
                      {hire.insurancePlan || "none"}
                    </Text>
                    <Text fontSize="sm" color={theme.text}>
                      <Text as="span" fontWeight="700">
                        Customer:
                      </Text>{" "}
                      {loggedInUserId ? "Logged in" : "Guest checkout"}
                    </Text>
                  </Stack>
                </Stack>
                <OrderPricingBreakdownCard
                  breakdown={pricingBreakdown}
                  fallbackTotal={checkoutAmount}
                  title="Hire Breakdown"
                />
              </Stack>
            </Collapse>
          </Box>
          <Divider my={4} borderColor={theme.divider} />
          <Text fontWeight="semibold" mb={2} color={theme.text}>
            Delivery
          </Text>
          <HStack justify="space-between" mb={2}>
            <Text color={theme.text}>Deliver to customer?</Text>
            <Switch
              isChecked={deliveryRequested}
              onChange={(e) => setDeliveryRequested(e.target.checked)}
            />
          </HStack>
          {deliveryRequested && (
            <Box>
              <FormLabel color={theme.text}>Delivery address</FormLabel>
              {loadError || !API_KEY ? (
                <Input
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Delivery address"
                  {...inputStyles}
                />
              ) : !isLoaded ? (
                <Input
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Loading..."
                  {...inputStyles}
                />
              ) : (
                <AutocompleteInput
                  placeholder="Delivery address"
                  value={deliveryAddress}
                  onPlaceSelected={(addr: string) => setDeliveryAddress(addr)}
                  onLocationSelected={(coords: { lat: number; lng: number }) =>
                    setDeliveryCoords(coords)
                  }
                />
              )}
            </Box>
          )}
          <Divider my={4} borderColor={theme.divider} />
          <Text fontWeight="semibold" mb={2} color={theme.text}>
            Compliance & Damage Policy
          </Text>
          <FormControl
            mb={3}
            isRequired
            isInvalid={!isPaymentTransitioning && !hasDrivingLicenceDocument}
          >
            <FormLabel color={theme.text}>
              Driving licence document (required, max 2)
            </FormLabel>
            <Text mb={2} fontSize="xs" color={theme.textSubtle}>
              Upload a clear image or PDF of the customer&apos;s valid driving
              licence. Maximum file size is 10 MB per document.
            </Text>
            <Input
              type="file"
              ref={licenseInputRef}
              display="none"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,application/pdf,image/*"
              onChange={handlePickLicenseFiles}
            />
            <HStack align="stretch">
              <Input
                value={
                  licenseFiles.length > 0
                    ? `${licenseFiles.length} new licence document(s) selected`
                    : licenseDocumentUrls.join(", ")
                }
                readOnly
                placeholder="No driving licence document selected"
                {...inputStyles}
              />
              <Button
                onClick={() => licenseInputRef.current?.click()}
                color={theme.textOnPrimary}
                bgImage={theme.gradients.primaryButton}
                _hover={{
                  bgImage: theme.gradients.primaryButtonHover,
                }}
              >
                Select Files
              </Button>
            </HStack>
            {!isPaymentTransitioning && !hasDrivingLicenceDocument && (
              <Text mt={1} fontSize="sm" color="red.500">
                Driving licence document is compulsory.
              </Text>
            )}
            {licenseFiles.length > 0 && (
              <Box mt={3}>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color={theme.text}
                  mb={2}
                >
                  Driving licence preview before checkout
                </Text>
                <Wrap spacing={3}>
                  {licenseFiles.map((item, idx) => (
                    <WrapItem key={`${item.name}-${idx}`}>
                      <Box
                        p={3}
                        border="1px solid"
                        borderColor={theme.border}
                        borderRadius={cardsBorderRadii}
                        bg={theme.surfaceAlt}
                        minW="180px"
                        maxW="220px"
                      >
                        <Text
                          fontSize="xs"
                          color={theme.textSubtle}
                          noOfLines={2}
                        >
                          {item.name}
                        </Text>
                        {item.type === "image" && (
                          <Image
                            src={item.previewUrl}
                            alt={item.name}
                            mt={3}
                            h="120px"
                            w="100%"
                            objectFit="contain"
                            borderRadius={cardsBorderRadii}
                            border="1px solid"
                            borderColor={theme.border}
                          />
                        )}
                        {item.type === "pdf" && (
                          <Box mt={3}>
                            <Text fontSize="sm" color={theme.textMuted}>
                              PDF selected
                            </Text>
                            <Link
                              href={item.previewUrl}
                              isExternal
                              color={theme.primary}
                              fontSize="sm"
                            >
                              Open PDF preview
                            </Link>
                          </Box>
                        )}
                        {!isPaymentTransitioning && (
                          <Button
                            mt={3}
                            size="xs"
                            colorScheme="red"
                            onClick={() => removeLicenseAt(idx)}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}
            {!isPaymentTransitioning &&
              licenseDocumentUrls.length > 0 &&
              licenseFiles.length === 0 && (
                <Box mt={3}>
                  <Text fontSize="xs" color={theme.textSubtle}>
                    Uploaded driving licence document(s):{" "}
                    {licenseDocumentUrls.length}
                  </Text>
                  <Button
                    mt={2}
                    size="xs"
                    colorScheme="red"
                    onClick={clearUploadedLicenseUrls}
                  >
                    Remove uploaded licence and re-upload
                  </Button>
                </Box>
              )}
          </FormControl>
          <FormControl
            mb={3}
            isRequired
            isInvalid={!isPaymentTransitioning && !hasIdentityDocument}
          >
            <FormLabel color={theme.text}>
              ID or passport (required, max 2)
            </FormLabel>
            <Text mb={2} fontSize="xs" color={theme.textSubtle}>
              Upload a clear national ID or passport image/PDF. Maximum file
              size is 10 MB per document.
            </Text>
            <Input
              type="file"
              ref={identityInputRef}
              display="none"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,application/pdf,image/*"
              onChange={handlePickIdentityFiles}
            />
            <HStack align="stretch">
              <Input
                value={
                  identityFiles.length > 0
                    ? `${identityFiles.length} new ID/passport document(s) selected`
                    : identityDocumentUrls.join(", ")
                }
                readOnly
                placeholder="No ID or passport selected"
                {...inputStyles}
              />
              <Button
                onClick={() => identityInputRef.current?.click()}
                color={theme.textOnPrimary}
                bgImage={theme.gradients.primaryButton}
                _hover={{
                  bgImage: theme.gradients.primaryButtonHover,
                }}
              >
                Select Files
              </Button>
            </HStack>
            {!isPaymentTransitioning && !hasIdentityDocument && (
              <Text mt={1} fontSize="sm" color="red.500">
                ID or passport is compulsory.
              </Text>
            )}
            {identityFiles.length > 0 && (
              <Box mt={3}>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color={theme.text}
                  mb={2}
                >
                  ID / passport preview before checkout
                </Text>
                <Wrap spacing={3}>
                  {identityFiles.map((item, idx) => (
                    <WrapItem key={`${item.name}-${idx}`}>
                      <Box
                        p={3}
                        border="1px solid"
                        borderColor={theme.border}
                        borderRadius={cardsBorderRadii}
                        bg={theme.surfaceAlt}
                        minW="180px"
                        maxW="220px"
                      >
                        <Text
                          fontSize="xs"
                          color={theme.textSubtle}
                          noOfLines={2}
                        >
                          {item.name}
                        </Text>
                        {item.type === "image" && (
                          <Image
                            src={item.previewUrl}
                            alt={item.name}
                            mt={3}
                            h="120px"
                            w="100%"
                            objectFit="contain"
                            borderRadius={cardsBorderRadii}
                            border="1px solid"
                            borderColor={theme.border}
                          />
                        )}
                        {item.type === "pdf" && (
                          <Box mt={3}>
                            <Text fontSize="sm" color={theme.textMuted}>
                              PDF selected
                            </Text>
                            <Link
                              href={item.previewUrl}
                              isExternal
                              color={theme.primary}
                              fontSize="sm"
                            >
                              Open PDF preview
                            </Link>
                          </Box>
                        )}
                        {!isPaymentTransitioning && (
                          <Button
                            mt={3}
                            size="xs"
                            colorScheme="red"
                            onClick={() => removeIdentityAt(idx)}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}
            {!isPaymentTransitioning &&
              identityDocumentUrls.length > 0 &&
              identityFiles.length === 0 && (
                <Box mt={3}>
                  <Text fontSize="xs" color={theme.textSubtle}>
                    Uploaded ID/passport document(s):{" "}
                    {identityDocumentUrls.length}
                  </Text>
                  <Button
                    mt={2}
                    size="xs"
                    colorScheme="red"
                    onClick={clearUploadedIdentityUrls}
                  >
                    Remove uploaded ID/passport and re-upload
                  </Button>
                </Box>
              )}
          </FormControl>
          <FormControl isInvalid={!isPaymentTransitioning && !damageAck}>
            <Box
              border="1px solid"
              borderColor={damageAck ? "green.400" : "orange.500"}
              bg={theme.surfaceAlt}
              borderRadius={cardsBorderRadii}
              p={{ base: 3, sm: 4 }}
              transition="all 0.2s ease"
            >
              <Checkbox
                size="lg"
                colorScheme="green"
                isChecked={damageAck}
                onChange={(e) => setDamageAck(e.target.checked)}
                alignItems="flex-start"
                sx={{
                  ".chakra-checkbox__control": {
                    mt: "2px",
                    borderWidth: "2px",
                    bg: "transparent",
                    borderColor: damageAck ? "green.500" : "orange.500",
                    _hover: {
                      bg: "transparent",
                      borderColor: damageAck ? "green.600" : "orange.600",
                    },
                    _checked: {
                      bg: "green.500",
                      borderColor: "green.500",
                      color: "white",
                    },
                  },
                }}
              >
                <Box pl={1}>
                  <HStack spacing={2} flexWrap="wrap">
                    <Text fontWeight="800" color={theme.text}>
                      Damage policy acknowledgement
                    </Text>
                  </HStack>
                </Box>
              </Checkbox>
              <Text
                mt={3}
                fontSize="sm"
                fontWeight="700"
                color={damageAck ? "green.500" : "orange.500"}
              >
                {damageAck
                  ? isComplianceReady
                    ? "Confirmed. All required compliance documents are ready."
                    : "Damage policy confirmed. Add the required licence and ID/passport documents to enable payment."
                  : "Check this box and complete the required documents to enable payment."}
              </Text>
            </Box>
          </FormControl>
          <Divider my={4} borderColor={theme.divider} />
          <HStack justify="space-between">
            <Text fontWeight="semibold" color={theme.text}>
              Total Due
            </Text>
            <Text fontSize="xl" fontWeight="bold" color={theme.text}>
              MWK {checkoutAmount.toLocaleString()}
            </Text>
          </HStack>
          {isSubmittingBooking && (
            <Text mt={3} fontSize="sm" color={theme.textMuted}>
              Finalizing booking...
            </Text>
          )}
        </Box>
        <Box
          flex="1"
          w="100%"
          minW={0}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowSm}
          border="1px solid"
          borderColor={theme.border}
          p={{ base: 3, md: 4 }}
        >
          <Heading size="sm" mb={4} color={theme.text}>
            Complete Payment
          </Heading>
          <fieldset
            disabled={!isComplianceReady || isSubmittingBooking}
            aria-disabled={!isComplianceReady || isSubmittingBooking}
            style={{
              border: 0,
              padding: 0,
              margin: 0,
              minWidth: 0,
              opacity: isComplianceReady ? 1 : 0.55,
              pointerEvents: isComplianceReady ? "auto" : "none",
              transition: "opacity 0.2s ease",
            }}
          >
            <PaymentCheckoutTest
              amount={checkoutAmount}
              onCreateOrder={onCreateOrder}
              shouldClearCart={false}
              pendingPath={(id: string) => `/checkout-pending/${id}`}
              successPath={(id: string) => `/checkout-success/${id}`}
            />
          </fieldset>
        </Box>
      </Stack>
    </Box>
  );
}
